import { WebDashboardContentSpecBlocksInner } from "@coeff/agent-types";
import { MSTeamsChannel, SlackChannel, SlackUser } from "@coeff/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { QueryCacheKey } from "../../../api/queryClient";
import {
  CloseIcon,
  ConnectPlugIcon,
  DeleteIcon,
  EllipsisMenu,
  PauseIcon,
  PencilIcon,
  ResumeIcon,
} from "../../../components";
import { isValidEmail, useApiContext } from "../../../utils";
import { getBlockTypeIcon, resolveBlockTitle } from "../BlockAutocompleteDropdown";

import {
  DESTINATION_TYPE_OPTIONS,
  DashboardSubscription,
  SubscriptionDestination,
  SubscriptionDestinationType,
  WEEKDAYS,
} from "./types";

// ─── Shared dropdown hook ─────────────────────────────────────────────────────

const useFixedDropdown = (align: "left" | "right" = "right") => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties | undefined>(undefined);

  const computeStyle = useCallback(
    (btn: HTMLButtonElement): React.CSSProperties => {
      const ownerWin = btn.ownerDocument.defaultView;
      const rect = btn.getBoundingClientRect();
      return {
        position: "fixed",
        top: rect.bottom + 4,
        ...(align === "right"
          ? { right: (ownerWin?.innerWidth ?? 0) - rect.right }
          : { left: rect.left }),
      };
    },
    [align]
  );

  const toggle = useCallback(() => {
    setIsOpen(prev => {
      if (!prev && buttonRef.current) {
        setStyle(computeStyle(buttonRef.current));
      }
      return !prev;
    });
  }, [computeStyle]);

  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    const ownerDoc = buttonRef.current?.ownerDocument ?? document;
    ownerDoc.addEventListener("mousedown", handleClickOutside, true);
    return () => ownerDoc.removeEventListener("mousedown", handleClickOutside, true);
  }, [isOpen]);

  return { isOpen, toggle, close, style, buttonRef, menuRef };
};

// ─── Icons ────────────────────────────────────────────────────────────────────

export const SlackLogo: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 54 54" fill="none" aria-hidden className="shrink-0">
    <path
      d="M19.712.133a5.381 5.381 0 0 0-5.376 5.387 5.381 5.381 0 0 0 5.376 5.386h5.376V5.52A5.381 5.381 0 0 0 19.712.133m0 14.365H5.376A5.381 5.381 0 0 0 0 19.884a5.381 5.381 0 0 0 5.376 5.387h14.336a5.381 5.381 0 0 0 5.376-5.387 5.381 5.381 0 0 0-5.376-5.386"
      fill="#36C5F0"
    />
    <path
      d="M53.76 19.884a5.381 5.381 0 0 0-5.376-5.386 5.381 5.381 0 0 0-5.376 5.386v5.387h5.376a5.381 5.381 0 0 0 5.376-5.387m-14.336 0V5.52A5.381 5.381 0 0 0 34.048.133a5.381 5.381 0 0 0-5.376 5.387v14.364a5.381 5.381 0 0 0 5.376 5.387 5.381 5.381 0 0 0 5.376-5.387"
      fill="#2EB67D"
    />
    <path
      d="M34.048 54a5.381 5.381 0 0 0 5.376-5.387 5.381 5.381 0 0 0-5.376-5.386h-5.376v5.386A5.381 5.381 0 0 0 34.048 54m0-14.365h14.336a5.381 5.381 0 0 0 5.376-5.386 5.381 5.381 0 0 0-5.376-5.387H34.048a5.381 5.381 0 0 0-5.376 5.387 5.381 5.381 0 0 0 5.376 5.386"
      fill="#ECB22E"
    />
    <path
      d="M0 34.249a5.381 5.381 0 0 0 5.376 5.386 5.381 5.381 0 0 0 5.376-5.386v-5.387H5.376A5.381 5.381 0 0 0 0 34.249m14.336 0v14.364A5.381 5.381 0 0 0 19.712 54a5.381 5.381 0 0 0 5.376-5.387V34.249a5.381 5.381 0 0 0-5.376-5.387 5.381 5.381 0 0 0-5.376 5.387"
      fill="#E01E5A"
    />
  </svg>
);

export const TeamsMicrosoftIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 2228.833 2073.333"
    fill="none"
    aria-hidden
    className="shrink-0"
  >
    <path
      d="M1554.637 777.5h575.713c54.391 0 98.483 44.092 98.483 98.483v524.398c0 199.901-162.051 361.952-361.952 361.952h-1.711c-199.901.028-361.975-162.023-361.975-361.924V828.971a51.468 51.468 0 0 1 51.442-51.471z"
      fill="#5059C9"
    />
    <circle cx="1943.75" cy="440.583" r="233.25" fill="#5059C9" />
    <circle cx="1218.083" cy="336.917" r="336.917" fill="#7B83EB" />
    <path
      d="M1667.323 777.5H717.01c-53.743 1.33-96.257 45.931-94.927 99.676v598.105c-7.825 322.519 247.836 590.284 570.355 598.104 322.519-7.82 578.18-275.585 570.355-598.104V877.176c1.33-53.745-41.184-98.346-94.927-99.676z"
      fill="#7B83EB"
    />
    <path
      opacity=".1"
      d="M1242 777.5v838.145c-.397 44.479-27.768 84.019-69.012 101.556a108.944 108.944 0 0 1-42.071 8.358H594.984a234.2 234.2 0 0 1-9.8-67.803V877.147c-1.33-53.726 41.162-98.309 94.888-99.647H1242z"
      fill="#000"
    />
    <path
      opacity=".2"
      d="M1192 777.5v888.145c0 14.414-2.832 28.688-8.358 42.071-17.537 41.244-57.077 68.615-101.556 69.012H639.929a286.007 286.007 0 0 1-16.945-44.86 234.2 234.2 0 0 1-9.8-67.803V877.147c-1.33-53.726 41.162-98.309 94.888-99.647H1192z"
      fill="#000"
    />
    <path
      opacity=".2"
      d="M1192 777.5v788.145c-.363 52.298-43.054 94.637-95.352 94.628H639.929a234.2 234.2 0 0 1-9.8-67.803V877.147c-1.33-53.726 41.162-98.309 94.888-99.647H1192z"
      fill="#000"
    />
    <path
      opacity=".2"
      d="M1142 777.5v788.145c-.363 52.298-43.054 94.637-95.352 94.628H639.929a234.2 234.2 0 0 1-9.8-67.803V877.147c-1.33-53.726 41.162-98.309 94.888-99.647H1142z"
      fill="#000"
    />
    <path
      d="M98.29 777.5h950.42c54.302 0 98.29 43.988 98.29 98.29v950.42c0 54.302-43.988 98.29-98.29 98.29H98.29C43.988 1924.5 0 1880.512 0 1826.21V875.79C0 821.488 43.988 777.5 98.29 777.5z"
      fill="#5059C9"
    />
    <path
      d="M820.211 1090.833H630.056v517.297H509.417v-517.297H320.789V970.167h499.422v120.666z"
      fill="#fff"
    />
  </svg>
);

export const DestinationIcon: React.FC<{
  type: SubscriptionDestinationType;
  size?: "sm" | "md";
}> = ({ type, size = "md" }) => {
  const iconPx = size === "sm" ? 12 : 14;
  return (
    <span
      className={`flex shrink-0 items-center justify-center rounded-full bg-main-background ${
        // Explicit px (not h-5/w-5) so the subscription list item's icons are a true 20×20 —
        // rem utilities render at 87.5% inside the dashboard viewer iframe (14px text-sm root).
        size === "sm" ? "h-[20px] w-[20px]" : "h-6 w-6"
      }`}
    >
      {type === "slack" ? (
        <SlackLogo size={iconPx} />
      ) : type === "teams" ? (
        <TeamsMicrosoftIcon size={iconPx} />
      ) : (
        <span className="material-icons" style={{ color: "#FAAD14", fontSize: iconPx + 2 }}>
          email
        </span>
      )}
    </span>
  );
};

// ─── Chips ────────────────────────────────────────────────────────────────────

/** Removable tag chip (matches the Figma Tag component) — used for recipients, channels, and
 * selected blocks throughout the Subscribe modal. */
export const Chip: React.FC<{ label: string; onRemove: () => void }> = ({ label, onRemove }) => (
  // Explicit px sizing (not rem utilities): chips match the Figma Tag spec exactly, immune to
  // the iframe's 14px-root rem scaling.
  <span className="inline-flex items-center gap-[8px] h-[24px] px-[8px] bg-main-chip-background rounded-[4px] text-[14px] font-medium leading-[18px] text-main-foreground">
    <span className="truncate max-w-[180px]">{label}</span>
    <button
      type="button"
      onClick={e => {
        e.stopPropagation();
        onRemove();
      }}
      className="flex items-center justify-center size-[16px] -mr-[2px] rounded text-main-foreground hover:opacity-70 cursor-pointer transition-opacity"
      aria-label={`Remove ${label}`}
    >
      <CloseIcon width="10" height="10" />
    </button>
  </span>
);

// ─── Segment tabs (Material style toggle group) ──────────────────────────────

interface SegmentTabsProps<T extends string> {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  size?: "sm" | "lg";
}

export function SegmentTabs<T extends string>({
  options,
  value,
  onChange,
  size = "sm",
}: SegmentTabsProps<T>) {
  const isLg = size === "lg";
  const containerClass = isLg ? "rounded-lg px-0.5 py-[3px]" : "rounded-md p-[3px]";
  const tabSizeClass = isLg ? "h-10 rounded-lg" : "h-7 rounded";
  return (
    <div
      className={`inline-flex w-full max-w-full items-center bg-main-background-tertiary ${containerClass}`}
    >
      {options.map(opt => {
        const tabClass =
          opt.value === value
            ? "bg-main-background text-primary-button-background"
            : "text-main-foreground hover:opacity-80";
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`flex min-w-0 flex-1 items-center justify-center px-2 text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${tabSizeClass} ${tabClass}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Select dropdown ──────────────────────────────────────────────────────────

export const SelectDropdown: React.FC<{
  options: string[];
  value: string;
  onChange: (v: string) => void;
  /** Narrow trigger (e.g. time row under schedule tabs). */
  compact?: boolean;
  /** Solid caret instead of Material chevron. */
  caret?: "chevron" | "triangle";
  className?: string;
}> = ({ options, value, onChange, compact, caret = "chevron", className }) => {
  const listboxId = useMemo(
    () => `subscription-select-${Math.random().toString(36).slice(2, 11)}`,
    []
  );
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | undefined>(undefined);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const positionMenu = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) {
      return;
    }
    const rect = btn.getBoundingClientRect();
    setMenuStyle({
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.left,
      minWidth: rect.width,
      maxWidth: "min(100vw - 16px, 320px)",
    });
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  const openToggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  useLayoutEffect(() => {
    if (isOpen) {
      positionMenu();
    }
  }, [isOpen, value, positionMenu]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onReposition = () => positionMenu();
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [isOpen, positionMenu]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const ownerDoc = buttonRef.current?.ownerDocument ?? document;
    const handlePointerDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (buttonRef.current?.contains(t) || menuRef.current?.contains(t)) {
        return;
      }
      close();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close();
      }
    };
    ownerDoc.addEventListener("mousedown", handlePointerDown, true);
    ownerDoc.addEventListener("keydown", handleKeyDown, true);
    return () => {
      ownerDoc.removeEventListener("mousedown", handlePointerDown, true);
      ownerDoc.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isOpen, close]);

  return (
    <div
      className={`relative inline-block min-w-0 ${
        compact ? "w-full max-w-[min(100%,17rem)]" : ""
      } ${className ?? ""}`}
    >
      <button
        type="button"
        ref={buttonRef}
        id={listboxId}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? `${listboxId}-listbox` : undefined}
        onClick={openToggle}
        className={`group flex h-10 w-full min-w-[10rem] items-center justify-between gap-2 rounded-lg border bg-main-background px-3 text-left text-sm text-main-foreground transition-colors ${
          compact ? "max-w-full" : "max-w-[min(100%,18rem)]"
        } ${
          isOpen
            ? "border-primary-button-background ring-1 ring-primary-button-background"
            : "border-main-border hover:border-main-foreground/40"
        } focus:outline-none`}
      >
        <span className="min-w-0 flex-1 truncate font-medium tabular-nums">{value}</span>
        {caret === "triangle" ? (
          <svg
            width="10"
            height="7"
            viewBox="0 0 10 7"
            className={`shrink-0 fill-current text-main-foreground transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
            aria-hidden
          >
            <path d="M0 0h10L5 7z" />
          </svg>
        ) : (
          <span
            className={`material-icons shrink-0 !text-lg text-main-muted-foreground transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          >
            arrow_drop_down
          </span>
        )}
      </button>
      {isOpen && menuStyle && (
        <div
          ref={menuRef}
          id={`${listboxId}-listbox`}
          role="listbox"
          aria-labelledby={listboxId}
          className="z-[500] max-h-56 overflow-y-auto rounded-lg border border-main-border bg-main-background py-1 shadow-[0_4px_24px_rgba(0,0,0,0.12)]"
          style={menuStyle}
        >
          {options.map(opt => {
            const selected = opt === value;
            return (
              <button
                key={opt}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(opt);
                  close();
                }}
                className={`flex w-full min-w-0 items-center justify-between px-3 py-2.5 text-left text-sm transition-colors ${
                  selected
                    ? "bg-primary-button-background/8 text-primary-button-background font-medium"
                    : "text-main-foreground hover:bg-main-text-button-hover-background"
                }`}
              >
                <span className="min-w-0 break-words tabular-nums">{opt}</span>
                {selected && (
                  <span className="material-icons !text-base text-primary-button-background ml-2 shrink-0">
                    check
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Toggle switch ────────────────────────────────────────────────────────────

export const Toggle: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  id?: string;
}> = ({ checked, onChange, id }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    id={id}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-button-background focus-visible:ring-offset-2 ${
      checked ? "bg-primary-button-background" : "bg-main-border"
    }`}
  >
    <span
      className={`inline-block w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${
        checked ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
);

// ─── Section header ───────────────────────────────────────────────────────────

export const SectionHeader: React.FC<{
  icon: React.ReactNode;
  label: string;
  action?: React.ReactNode;
}> = ({ icon, label, action }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2 text-main-foreground">
      {icon}
      <span className="text-sm font-semibold text-main-foreground">{label}</span>
    </div>
    {action && <div>{action}</div>}
  </div>
);

/** Multiple days of week (`days_of_week` on the API). At least one day must stay selected. */
export const WeeklyDayRow: React.FC<{
  weekdays: number[];
  onChange: (weekdays: number[]) => void;
}> = ({ weekdays, onChange }) => {
  const selected = useMemo(() => new Set(weekdays), [weekdays]);

  const toggle = (i: number) => {
    if (selected.has(i) && selected.size <= 1) {
      return;
    }
    if (selected.has(i)) {
      onChange(weekdays.filter(d => d !== i));
    } else {
      onChange([...weekdays, i].sort((a, b) => a - b));
    }
  };

  return (
    <div className="grid w-full grid-cols-7 gap-1.5" role="group" aria-label="Days of week">
      {/* Mon-first display order (per design); underlying indices stay 0–6 Sun–Sat. */}
      {[1, 2, 3, 4, 5, 6, 0].map(dow => {
        const fullName = WEEKDAYS[dow];
        const active = selected.has(dow);
        const short = fullName.slice(0, 3);
        return (
          <button
            key={fullName}
            type="button"
            role="checkbox"
            aria-checked={active}
            title={fullName}
            onClick={() => toggle(dow)}
            className={`min-h-[36px] rounded-lg text-sm font-medium transition-colors cursor-pointer ${
              active
                ? "bg-primary-button-background text-primary-button-foreground"
                : "text-main-foreground hover:bg-main-background-tertiary/60"
            }`}
          >
            {short}
          </button>
        );
      })}
    </div>
  );
};

/** Month days 1–30 + “Last day” (sentinel day 31, matches scheduling_utils month-end). */
export const MonthlyDayGrid: React.FC<{
  value: string;
  onChange: (serialized: string) => void;
}> = ({ value, onChange }) => {
  const selected = useMemo(() => {
    const set = new Set<number>();
    for (const part of value.split(",")) {
      const n = parseInt(part.trim(), 10);
      if (Number.isInteger(n) && n >= 1 && n <= 31) {
        set.add(n);
      }
    }
    return set;
  }, [value]);

  const LAST_DAY_DOM = 31;

  const commit = (next: Set<number>) => {
    const sorted = Array.from(next).sort((a, b) => a - b);
    onChange(sorted.join(", "));
  };

  const toggleDay = (day: number) => {
    const next = new Set(selected);
    if (next.has(day)) {
      next.delete(day);
    } else {
      next.add(day);
    }
    commit(next);
  };

  const dayButtonClass = (day: number, active: boolean) =>
    `min-h-[36px] rounded-lg text-sm font-medium transition-colors cursor-pointer ${
      active
        ? "bg-primary-button-background text-primary-button-foreground"
        : "text-main-foreground hover:bg-main-background-tertiary/60"
    }`;

  const lastSelected = selected.has(LAST_DAY_DOM);

  return (
    <div className="grid grid-cols-7 gap-1.5 w-full">
      {Array.from({ length: 30 }, (_, i) => {
        const day = i + 1;
        const active = selected.has(day);
        return (
          <button
            key={day}
            type="button"
            onClick={() => toggleDay(day)}
            className={dayButtonClass(day, active)}
          >
            {day}
          </button>
        );
      })}
      <button
        type="button"
        onClick={() => toggleDay(LAST_DAY_DOM)}
        className={`col-span-5 rounded-lg text-sm font-medium transition-colors cursor-pointer px-2 ${
          lastSelected
            ? "bg-primary-button-background text-primary-button-foreground"
            : "text-main-foreground hover:bg-main-background-tertiary/60"
        }`}
      >
        Last day of month
      </button>
    </div>
  );
};

// ─── Recipient input box ──────────────────────────────────────────────────────

export const RecipientInputBox: React.FC<{
  placeholder?: string;
  chips: string[];
  onAdd: (value: string) => void;
  onRemove: (value: string) => void;
  validateValue?: (value: string) => boolean;
  formatValidationError?: (invalidValues: string[]) => string;
}> = ({
  placeholder = "Add",
  chips,
  onAdd,
  onRemove,
  validateValue,
  formatValidationError = invalidValues =>
    `Invalid value${invalidValues.length > 1 ? "s" : ""}: ${invalidValues.join(", ")}`,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const commit = (raw: string) => {
    const values = raw
      .split(/[,;\s]+/)
      .map(value =>
        value
          .trim()
          .replace(/^[#@]/, "")
          .replace(/[,;]+$/, "")
      )
      .filter(Boolean);

    if (values.length === 0) {
      setInputValue("");
      setValidationError(null);
      return;
    }

    const invalidValues = validateValue ? values.filter(value => !validateValue(value)) : [];
    if (invalidValues.length > 0) {
      setValidationError(formatValidationError(invalidValues));
      return;
    }

    setValidationError(null);
    for (const value of values) {
      if (!chips.includes(value)) {
        onAdd(value);
      }
    }
    setInputValue("");
  };

  return (
    <div className="flex flex-col gap-1">
      <div
        className={`flex flex-wrap items-center gap-1.5 px-1 py-1 min-h-[35px] border rounded-lg bg-main-background cursor-text transition-colors ${
          validationError
            ? "border-red-500 focus-within:ring-1 focus-within:ring-red-500"
            : "border-main-border hover:border-main-foreground/40 focus-within:border-primary-button-background focus-within:ring-1 focus-within:ring-primary-button-background"
        }`}
        onClick={() => inputRef.current?.focus()}
      >
        {chips.map(chip => (
          <Chip key={chip} label={chip} onRemove={() => onRemove(chip)} />
        ))}
        <input
          ref={inputRef}
          type="text"
          placeholder={chips.length === 0 ? placeholder : "Add"}
          value={inputValue}
          onChange={e => {
            setInputValue(e.target.value);
            if (validationError) {
              setValidationError(null);
            }
          }}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              commit(inputValue);
            } else if (e.key === "Backspace" && !inputValue && chips.length > 0) {
              onRemove(chips[chips.length - 1]);
            }
          }}
          onBlur={() => {
            if (inputValue.trim()) {
              commit(inputValue);
            }
          }}
          className="flex-1 min-w-[60px] border-0 bg-transparent px-1 py-1 text-sm text-main-foreground placeholder:text-main-muted-foreground focus:outline-none focus:ring-0"
        />
      </div>
      {validationError && (
        <div className="flex items-center gap-1 text-xs text-red-600 px-1">
          <span className="material-icons !text-sm">error_outline</span>
          {validationError}
        </div>
      )}
    </div>
  );
};

// ─── Block picker ─────────────────────────────────────────────────────────────

const BLOCK_PREVIEW_COLORS: Record<string, string> = {
  chart: "#3B82F6",
  metric_card: "#8B5CF6",
  data_table: "#10B981",
  ai_image: "#F59E0B",
  ai_insights: "#EC4899",
  section_header: "#6B7280",
  text_block: "#6B7280",
};

const PREVIEW_W = 176;
const PREVIEW_MAX_H = 120;

const NO_CANVAS_BLOCK_TYPES = new Set(["text_block", "section_header"]);

type CaptureResult = { status: "loading" } | { status: "ready"; src: string } | { status: "error" };
type PreviewCache = Map<string, CaptureResult>;

const captureBlock = (blockId: string): Promise<string | null> => {
  try {
    const iframeEl = document.querySelector("iframe");
    const iframeWin = iframeEl?.contentWindow;
    const iframeDoc = iframeEl?.contentDocument;
    if (!iframeWin || !iframeDoc) {
      return Promise.resolve(null);
    }

    const el = iframeDoc.getElementById(`block_${blockId}`);
    if (!el) {
      return Promise.resolve(null);
    }

    const h2c = (iframeWin as any).html2canvas as
      | ((el: HTMLElement, opts: object) => Promise<HTMLCanvasElement>)
      | undefined;
    if (!h2c) {
      return Promise.resolve(null);
    }

    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      return Promise.resolve(null);
    }

    const scale = (PREVIEW_W / rect.width) * (window.devicePixelRatio || 2);
    const captureH = Math.min(
      rect.height,
      (PREVIEW_MAX_H / scale) * (window.devicePixelRatio || 2)
    );

    return h2c(el, {
      scale,
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: "#ffffff",
      height: captureH,
      windowHeight: captureH,
    })
      .then(canvas => {
        try {
          return canvas.toDataURL("image/png");
        } catch {
          return null;
        }
      })
      .catch(() => null);
  } catch {
    return Promise.resolve(null);
  }
};

const getTextBlockPreviewContent = (block: WebDashboardContentSpecBlocksInner): string | null => {
  if (block.type === "section_header") {
    return (block as any).title ?? null;
  }
  if (block.type === "text_block") {
    const md = (block as any).markdown as string | undefined;
    if (!md) {
      return null;
    }
    return md
      .replace(/[#*_~`>\-|[\]()!]/g, "")
      .replace(/\n+/g, " ")
      .trim()
      .slice(0, 80);
  }
  return null;
};

const PreviewSkeleton: React.FC<{ color: string }> = ({ color }) => (
  <div className="flex flex-col gap-1.5 px-3 py-3">
    <div className="h-1.5 rounded-full w-full" style={{ background: color, opacity: 0.2 }} />
    <div className="h-1.5 rounded-full w-3/4" style={{ background: color, opacity: 0.14 }} />
    <div className="h-1.5 rounded-full w-1/2" style={{ background: color, opacity: 0.1 }} />
    <div className="h-8 rounded mt-1 w-full" style={{ background: color, opacity: 0.08 }} />
  </div>
);

const BlockPreview: React.FC<{
  block: WebDashboardContentSpecBlocksInner;
  title: string;
  cache: PreviewCache;
}> = ({ block, title, cache }) => {
  const color = BLOCK_PREVIEW_COLORS[block.type] ?? "#6B7280";
  const icon = getBlockTypeIcon(block.type);
  const blockId = (block as any).block_id as string | undefined;
  const cacheEntry = blockId ? cache.get(blockId) : undefined;
  const isTextType = NO_CANVAS_BLOCK_TYPES.has(block.type);
  const textContent = isTextType ? getTextBlockPreviewContent(block) : null;
  const [imgError, setImgError] = useState(false);

  const status = isTextType ? "text" : cacheEntry?.status ?? "loading";
  const imgSrc = cacheEntry?.status === "ready" ? cacheEntry.src : undefined;

  const renderBody = () => {
    if (status === "loading") {
      return (
        <div className="flex items-center justify-center h-16">
          <span className="material-icons !text-base text-main-muted-foreground animate-spin">
            refresh
          </span>
        </div>
      );
    }

    if (status === "text") {
      return (
        <div className="px-3 py-2.5">
          {block.type === "section_header" ? (
            <div className="text-xs font-semibold text-main-foreground truncate">
              {textContent || title}
            </div>
          ) : (
            <p className="text-[11px] text-main-muted-foreground leading-relaxed line-clamp-3">
              {textContent || "Text content"}
            </p>
          )}
        </div>
      );
    }

    if ((status === "ready" || status === "error") && imgSrc && !imgError) {
      return (
        <img
          src={imgSrc}
          alt={title}
          className="w-full block"
          style={{ maxHeight: PREVIEW_MAX_H, objectFit: "cover", objectPosition: "top" }}
          draggable={false}
          onError={() => setImgError(true)}
        />
      );
    }

    if (status === "error" || imgError) {
      return (
        <div className="flex flex-col items-center justify-center h-16 gap-1">
          <span className="material-icons !text-base text-main-muted-foreground">broken_image</span>
          <span className="text-[11px] text-main-muted-foreground">Preview unavailable</span>
        </div>
      );
    }

    return <PreviewSkeleton color={color} />;
  };

  return (
    <div
      className="bg-main-background rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.12)] overflow-hidden pointer-events-none border border-main-border"
      style={{ width: PREVIEW_W }}
    >
      <div className="h-1 w-full" style={{ background: color }} />
      <div className="flex items-center gap-1.5 px-2 pt-1.5 pb-1 bg-main-background">
        <span className="material-icons !text-[13px] shrink-0" style={{ color }}>
          {icon}
        </span>
        <span className="text-[11px] font-semibold text-main-foreground truncate">{title}</span>
      </div>
      {renderBody()}
    </div>
  );
};

const getBlockTitle = resolveBlockTitle;

export const BlockPicker: React.FC<{
  blocks: WebDashboardContentSpecBlocksInner[];
  selectedIds: string[];
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
}> = ({ blocks, selectedIds, onAdd, onRemove }) => {
  const [inputValue, setInputValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [dropdownStyle, setDropdownStyle] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const [previewStyle, setPreviewStyle] = useState<{ top: number; left: number } | null>(null);
  const [previewCache, setPreviewCache] = useState<PreviewCache>(new Map());
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const capturedRef = useRef(false);

  const blockWithIds = blocks
    .filter(block => Boolean(block.block_id))
    .map(block => ({ block, stableId: block.block_id }));

  const filtered = blockWithIds.filter(
    ({ block, stableId }) =>
      !selectedIds.includes(stableId) &&
      getBlockTitle(block).toLowerCase().includes(inputValue.toLowerCase())
  );

  const selectedBlockItems = blockWithIds.filter(({ stableId }) => selectedIds.includes(stableId));

  const setCacheEntry = (blockId: string, entry: CaptureResult) => {
    setPreviewCache(prev => {
      const next = new Map(prev);
      next.set(blockId, entry);
      return next;
    });
  };

  useEffect(() => {
    if (capturedRef.current) {
      return;
    }
    capturedRef.current = true;
    let cancelled = false;
    const capturable = blocks.filter(b => b.block_id && !NO_CANVAS_BLOCK_TYPES.has(b.type));
    if (capturable.length === 0) {
      return;
    }

    // Mark all capturable blocks as loading upfront
    setPreviewCache(prev => {
      const next = new Map(prev);
      for (const b of capturable) {
        next.set(b.block_id, { status: "loading" });
      }
      return next;
    });

    const runNext = (i: number) => {
      if (cancelled || i >= capturable.length) {
        return;
      }
      const b = capturable[i];

      if (b.type === "ai_image") {
        const imageUrl = (b as any).image_url as string | undefined;
        setCacheEntry(
          b.block_id,
          imageUrl ? { status: "ready", src: imageUrl } : { status: "error" }
        );
        runNext(i + 1);
        return;
      }

      setTimeout(() => {
        captureBlock(b.block_id)
          .then(src => {
            if (cancelled) {
              return;
            }
            setCacheEntry(b.block_id, src ? { status: "ready", src } : { status: "error" });
            runNext(i + 1);
          })
          .catch(() => {
            if (cancelled) {
              return;
            }
            setCacheEntry(b.block_id, { status: "error" });
            runNext(i + 1);
          });
      }, 0);
    };

    runNext(0);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDropdown = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownStyle({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setShowDropdown(true);
  };

  const closeDropdown = () => {
    setShowDropdown(false);
    setDropdownStyle(null);
    setHoveredIdx(null);
    setPreviewStyle(null);
  };

  const showDropdownRef = useRef(false);
  showDropdownRef.current = showDropdown;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!showDropdownRef.current) {
        return;
      }
      const target = e.target as Node;
      const insideContainer = containerRef.current?.contains(target) ?? false;
      const insideDropdown = dropdownRef.current?.contains(target) ?? false;
      if (!insideContainer && !insideDropdown) {
        setShowDropdown(false);
        setDropdownStyle(null);
        setHoveredIdx(null);
        setPreviewStyle(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => document.removeEventListener("mousedown", handleClickOutside, true);
  }, []);

  useEffect(() => {
    if (!showDropdown) {
      return;
    }
    const update = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDropdownStyle({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
    };
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [showDropdown]);

  const handleRowMouseEnter = (e: React.MouseEvent<HTMLButtonElement>, idx: number) => {
    setHoveredIdx(idx);
    const rect = e.currentTarget.getBoundingClientRect();
    setPreviewStyle({ top: rect.top, left: rect.right + 12 });
  };

  const handleRowMouseLeave = () => {
    setHoveredIdx(null);
    setPreviewStyle(null);
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        className="flex flex-wrap items-center gap-1.5 px-1 py-1 min-h-[35px] border border-main-border rounded-lg bg-main-background cursor-text transition-colors hover:border-main-foreground/40 focus-within:border-primary-button-background focus-within:ring-1 focus-within:ring-primary-button-background"
        onClick={() => inputRef.current?.focus()}
      >
        {selectedBlockItems.map(({ block, stableId }) => (
          <Chip key={stableId} label={getBlockTitle(block)} onRemove={() => onRemove(stableId)} />
        ))}
        <input
          ref={inputRef}
          type="text"
          placeholder={selectedIds.length === 0 ? "Search blocks..." : "Add"}
          value={inputValue}
          onChange={e => {
            setInputValue(e.target.value);
            openDropdown();
          }}
          onFocus={() => openDropdown()}
          onBlur={e => {
            if (!dropdownRef.current?.contains(e.relatedTarget as Node)) {
              closeDropdown();
            }
          }}
          className="flex-1 min-w-[60px] border-0 bg-transparent p-0 text-sm text-main-foreground placeholder:text-main-muted-foreground focus:outline-none focus:ring-0 px-1"
        />
      </div>

      {showDropdown && filtered.length > 0 && dropdownStyle && (
        <div
          ref={dropdownRef}
          tabIndex={-1}
          className="fixed bg-main-background border border-main-border rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.12)] z-[500] max-h-72 overflow-y-auto outline-none"
          style={{ top: dropdownStyle.top, left: dropdownStyle.left, width: dropdownStyle.width }}
        >
          {filtered.map(({ block, stableId }, idx) => (
            <button
              key={stableId}
              type="button"
              onMouseDown={e => {
                e.preventDefault();
                onAdd(stableId);
                setInputValue("");
                setHoveredIdx(null);
                setPreviewStyle(null);
              }}
              onMouseEnter={e => handleRowMouseEnter(e, idx)}
              onMouseLeave={handleRowMouseLeave}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-main-foreground hover:bg-main-text-button-hover-background cursor-pointer text-left transition-colors"
            >
              <span className="material-icons !text-base text-main-muted-foreground">
                {getBlockTypeIcon(block.type)}
              </span>
              <span className="truncate">{getBlockTitle(block)}</span>
            </button>
          ))}
        </div>
      )}

      {hoveredIdx !== null && previewStyle && filtered[hoveredIdx] && (
        <div
          className="fixed z-[600] pointer-events-none"
          style={{ top: previewStyle.top, left: previewStyle.left }}
        >
          <BlockPreview
            block={filtered[hoveredIdx].block}
            title={getBlockTitle(filtered[hoveredIdx].block)}
            cache={previewCache}
          />
        </div>
      )}
    </div>
  );
};

// ─── Slack channel input box ──────────────────────────────────────────────────

const isSlackUserRecipientChip = (chip: string) => chip.trim().startsWith("@");

export const SlackChannelInputBox: React.FC<{
  chips: string[];
  onAdd: (channelName: string) => void;
  onRemove: (channelName: string) => void;
  isAuthorized?: boolean;
  autoOpenOnMount?: boolean;
}> = ({ chips, onAdd, onRemove, isAuthorized, autoOpenOnMount = false }) => {
  const { apiClient } = useApiContext();
  const [pickerTab, setPickerTab] = useState<"channels" | "people">("channels");
  const [inputValue, setInputValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hasAutoOpenedRef = useRef(false);

  const { data: channelsData, isLoading } = useQuery(
    [QueryCacheKey.SLACK_CHANNELS_AND_USERS],
    () => apiClient.getSlackChannelsAndUsers(),
    { enabled: !!isAuthorized, staleTime: 1000 * 60 * 5, retry: 1 }
  );

  const allChannels: SlackChannel[] = channelsData?.data.slack_channels ?? [];
  const allUsers: SlackUser[] = channelsData?.data.slack_users ?? [];

  const filteredChannels = allChannels.filter(
    ch =>
      !chips.includes(ch.channel_name) &&
      ch.channel_name.toLowerCase().includes(inputValue.replace(/^#/, "").toLowerCase())
  );

  const q = inputValue.replace(/^@/, "").toLowerCase();
  const filteredUsers = allUsers.filter(u => {
    const mention = `@${u.user_name}`;
    if (chips.includes(mention)) {
      return false;
    }
    const name = (u.user_name || "").toLowerCase();
    const real = (u.user_real_name || "").toLowerCase();
    return !q || name.includes(q) || real.includes(q);
  });

  const openDropdown = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownStyle({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setShowDropdown(true);
  };

  const closeDropdown = () => {
    setShowDropdown(false);
    setDropdownStyle(null);
  };

  useEffect(() => {
    const handleOut = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!containerRef.current?.contains(t) && !dropdownRef.current?.contains(t)) {
        closeDropdown();
      }
    };
    document.addEventListener("mousedown", handleOut, true);
    return () => document.removeEventListener("mousedown", handleOut, true);
  }, []);

  useEffect(() => {
    if (!showDropdown) {
      return;
    }
    const update = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDropdownStyle({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
    };
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [showDropdown]);

  useEffect(() => {
    if (!autoOpenOnMount || hasAutoOpenedRef.current) {
      return;
    }
    hasAutoOpenedRef.current = true;
    openDropdown();
    inputRef.current?.focus();
  }, [autoOpenOnMount]);

  const commitInput = (raw: string) => {
    const t = raw.trim();
    if (!t) {
      return;
    }
    if (pickerTab === "people" || isSlackUserRecipientChip(t)) {
      const at = t.startsWith("@") ? t : `@${t}`;
      if (!chips.includes(at)) {
        onAdd(at);
      }
    } else {
      const clean = t.replace(/^#/, "");
      if (clean && !chips.includes(clean)) {
        onAdd(clean);
      }
    }
    setInputValue("");
  };

  const placeholderEmpty = pickerTab === "channels" ? "Search channels..." : "Search people...";

  return (
    <div ref={containerRef} className="relative">
      <div className="flex gap-1 mb-1.5">
        <button
          type="button"
          onClick={() => setPickerTab("channels")}
          className={`text-xs font-semibold px-2 py-1 rounded-md transition-colors ${
            pickerTab === "channels"
              ? "bg-main-text-button-hover-background text-main-foreground"
              : "text-main-muted-foreground hover:text-main-foreground"
          }`}
        >
          Channels
        </button>
        <button
          type="button"
          onClick={() => setPickerTab("people")}
          className={`text-xs font-semibold px-2 py-1 rounded-md transition-colors ${
            pickerTab === "people"
              ? "bg-main-text-button-hover-background text-main-foreground"
              : "text-main-muted-foreground hover:text-main-foreground"
          }`}
        >
          People
        </button>
      </div>
      <div
        className="flex flex-wrap items-center gap-1.5 px-1 py-1 min-h-[35px] border border-main-border rounded-lg bg-main-background cursor-text transition-colors hover:border-main-foreground/40 focus-within:border-primary-button-background focus-within:ring-1 focus-within:ring-primary-button-background"
        onClick={() => inputRef.current?.focus()}
      >
        {chips.map(chip => (
          <Chip
            key={chip}
            label={isSlackUserRecipientChip(chip) ? chip : `#${chip}`}
            onRemove={() => onRemove(chip)}
          />
        ))}
        <input
          ref={inputRef}
          type="text"
          placeholder={chips.length === 0 ? placeholderEmpty : "Add"}
          value={inputValue}
          onChange={e => {
            setInputValue(e.target.value);
            openDropdown();
          }}
          onFocus={() => openDropdown()}
          onBlur={e => {
            if (!dropdownRef.current?.contains(e.relatedTarget as Node)) {
              closeDropdown();
            }
          }}
          onKeyDown={e => {
            if ((e.key === "Enter" || e.key === ",") && inputValue.trim()) {
              e.preventDefault();
              commitInput(inputValue);
            } else if (e.key === "Backspace" && !inputValue && chips.length > 0) {
              onRemove(chips[chips.length - 1]);
            }
          }}
          className="flex-1 min-w-[80px] border-0 bg-transparent px-1 py-1 text-sm text-main-foreground placeholder:text-main-muted-foreground focus:outline-none focus:ring-0"
        />
      </div>

      {showDropdown && dropdownStyle && (
        <div
          ref={dropdownRef}
          tabIndex={-1}
          className="fixed bg-main-background border border-main-border rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.12)] z-[500] max-h-60 overflow-y-auto outline-none"
          style={{ top: dropdownStyle.top, left: dropdownStyle.left, width: dropdownStyle.width }}
        >
          {isLoading ? (
            <div className="flex items-center gap-2 px-3 py-3 text-sm text-main-muted-foreground">
              <span className="material-icons !text-base animate-spin">refresh</span>
              Loading…
            </div>
          ) : pickerTab === "channels" ? (
            filteredChannels.length === 0 ? (
              <div className="px-3 py-3 text-sm text-main-muted-foreground">No channels found</div>
            ) : (
              filteredChannels.map(ch => (
                <button
                  key={ch.channel_id}
                  type="button"
                  onMouseDown={e => {
                    e.preventDefault();
                    commitInput(ch.channel_name);
                  }}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-main-foreground hover:bg-main-text-button-hover-background cursor-pointer text-left transition-colors"
                >
                  <span className="material-icons !text-sm text-main-muted-foreground">
                    {ch.is_private ? "lock" : "tag"}
                  </span>
                  <span className="flex-1 truncate">{ch.channel_name}</span>
                  {ch.is_private && (
                    <span className="text-[11px] text-main-muted-foreground bg-main-text-button-hover-background px-1.5 py-0.5 rounded-lg">
                      private
                    </span>
                  )}
                </button>
              ))
            )
          ) : filteredUsers.length === 0 ? (
            <div className="px-3 py-3 text-sm text-main-muted-foreground">No people found</div>
          ) : (
            filteredUsers.map(u => (
              <button
                key={u.user_id}
                type="button"
                onMouseDown={e => {
                  e.preventDefault();
                  const mention = `@${u.user_name}`;
                  if (!chips.includes(mention)) {
                    onAdd(mention);
                  }
                  setInputValue("");
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-main-foreground hover:bg-main-text-button-hover-background cursor-pointer text-left transition-colors"
              >
                <span className="material-icons !text-sm text-main-muted-foreground">person</span>
                <span className="flex-1 min-w-0 truncate">
                  <span className="block truncate">{u.user_real_name || u.user_name}</span>
                  <span className="block text-[11px] text-main-muted-foreground truncate">
                    @{u.user_name}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export const MSTeamsChannelInputBox: React.FC<{
  chips: string[];
  onAdd: (channelName: string) => void;
  onRemove: (channelName: string) => void;
  isAuthorized?: boolean;
  autoOpenOnMount?: boolean;
}> = ({ chips, onAdd, onRemove, isAuthorized, autoOpenOnMount = false }) => {
  const { apiClient } = useApiContext();
  const [inputValue, setInputValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [botMissingRecipients, setBotMissingRecipients] = useState<Set<string>>(() => new Set());
  const botInChannelCacheRef = useRef<Map<string, boolean | null>>(new Map());
  const botCheckInFlightRef = useRef<Map<string, Promise<boolean | null>>>(new Map());
  const mountedRef = useRef(true);
  const [dropdownStyle, setDropdownStyle] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hasAutoOpenedRef = useRef(false);

  const { data: channelsData, isLoading } = useQuery(
    [QueryCacheKey.MS_TEAMS_TEAMS_AND_CHANNELS],
    () => apiClient.getMSTeamsTeamsAndChannels(),
    { enabled: !!isAuthorized, staleTime: 1000 * 60 * 5, retry: 1 }
  );

  const allChannels: MSTeamsChannel[] = channelsData?.data.teams_and_channels ?? [];
  const filtered = allChannels.filter(ch => {
    const display = `${ch.team_name} / ${ch.channel_name}`;
    return !chips.includes(display) && display.toLowerCase().includes(inputValue.toLowerCase());
  });

  const openDropdown = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownStyle({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setShowDropdown(true);
  };

  const closeDropdown = () => {
    setShowDropdown(false);
    setDropdownStyle(null);
  };

  useEffect(() => {
    const handleOut = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!containerRef.current?.contains(t) && !dropdownRef.current?.contains(t)) {
        closeDropdown();
      }
    };
    document.addEventListener("mousedown", handleOut, true);
    return () => document.removeEventListener("mousedown", handleOut, true);
  }, []);

  useEffect(() => {
    if (!showDropdown) {
      return;
    }
    const update = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDropdownStyle({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
    };
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [showDropdown]);

  useEffect(() => {
    if (!autoOpenOnMount || hasAutoOpenedRef.current) {
      return;
    }
    hasAutoOpenedRef.current = true;
    openDropdown();
    inputRef.current?.focus();
  }, [autoOpenOnMount]);

  const groupedChannels = filtered.reduce<Record<string, MSTeamsChannel[]>>((acc, ch) => {
    if (!acc[ch.team_name]) {
      acc[ch.team_name] = [];
    }
    acc[ch.team_name].push(ch);
    return acc;
  }, {});

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // Remove warning entries for channels no longer selected.
    setBotMissingRecipients(prev => {
      const next = new Set<string>();
      for (const r of prev) {
        if (chips.includes(r)) {
          next.add(r);
        }
      }
      return next;
    });
  }, [chips]);

  const checkBotInChannelAndWarn = async (recipientDisplay: string, channelId: string) => {
    const cached = botInChannelCacheRef.current.get(channelId);
    if (cached === false) {
      setBotMissingRecipients(prev => {
        const next = new Set(prev);
        next.add(recipientDisplay);
        return next;
      });
      return;
    }
    if (cached === true) {
      return;
    }

    const inFlight = botCheckInFlightRef.current.get(channelId);
    const promise =
      inFlight ??
      (async () => {
        try {
          // IMPORTANT: use the same Axios instance as our API client so auth headers
          // (e.g. MICROSOFT-OAUTH-TOKEN / COEFF-AUTH-TOKEN) are attached consistently.
          const axiosInstance = (apiClient as any).axios as {
            post: (url: string, data?: unknown) => Promise<{ data?: any }>;
          };
          const resp = await axiosInstance.post("/api/ms_teams/bot_in_channel_check", {
            channel_id: channelId,
          });
          const data = (resp?.data ?? {}) as { bot_in_channel?: boolean };
          if (data.bot_in_channel === false) {
            return false;
          }
          if (data.bot_in_channel === true) {
            return true;
          }
          return null;
        } catch {
          return null;
        } finally {
          botCheckInFlightRef.current.delete(channelId);
        }
      })();

    botCheckInFlightRef.current.set(channelId, promise);
    const result = await promise;
    botInChannelCacheRef.current.set(channelId, result);
    if (!mountedRef.current) {
      return;
    }
    if (result === false) {
      setBotMissingRecipients(prev => {
        const next = new Set(prev);
        next.add(recipientDisplay);
        return next;
      });
    }
    try {
      // Best-effort check only; no UI changes on error.
    } catch {}
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        className="flex flex-wrap items-center gap-1.5 px-3 py-2 min-h-[35px] border border-main-border rounded-lg bg-main-background cursor-text transition-colors hover:border-main-foreground/40 focus-within:border-primary-button-background focus-within:ring-1 focus-within:ring-primary-button-background"
        onClick={() => inputRef.current?.focus()}
      >
        {chips.map(chip => (
          <Chip key={chip} label={chip} onRemove={() => onRemove(chip)} />
        ))}
        <input
          ref={inputRef}
          type="text"
          placeholder={chips.length === 0 ? "Search team channels..." : "Add"}
          value={inputValue}
          onChange={e => {
            setInputValue(e.target.value);
            openDropdown();
          }}
          onFocus={() => openDropdown()}
          onBlur={e => {
            if (!dropdownRef.current?.contains(e.relatedTarget as Node)) {
              closeDropdown();
            }
          }}
          onKeyDown={e => {
            if (e.key === "Backspace" && !inputValue && chips.length > 0) {
              onRemove(chips[chips.length - 1]);
            }
          }}
          className="flex-1 min-w-[80px] border-0 bg-transparent p-0 text-sm text-main-foreground placeholder:text-main-muted-foreground focus:outline-none focus:ring-0"
        />
      </div>

      {showDropdown && dropdownStyle && (
        <div
          ref={dropdownRef}
          tabIndex={-1}
          className="fixed bg-main-background border border-main-border rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.12)] z-[500] max-h-60 overflow-y-auto outline-none"
          style={{ top: dropdownStyle.top, left: dropdownStyle.left, width: dropdownStyle.width }}
        >
          {isLoading ? (
            <div className="flex items-center gap-2 px-3 py-3 text-sm text-main-muted-foreground">
              <span className="material-icons !text-base animate-spin">refresh</span>
              Loading channels...
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-3 text-sm text-main-muted-foreground">No channels found</div>
          ) : (
            Object.entries(groupedChannels).map(([teamName, channels]) => (
              <div key={teamName}>
                <div className="px-3 py-1.5 text-[11px] font-semibold text-main-muted-foreground uppercase tracking-wide bg-main-text-button-hover-background/60">
                  {teamName}
                </div>
                {channels.map(ch => {
                  const display = `${ch.team_name} / ${ch.channel_name}`;
                  return (
                    <button
                      key={ch.channel_id}
                      type="button"
                      onMouseDown={e => {
                        e.preventDefault();
                        onAdd(display);
                        setInputValue("");
                        void checkBotInChannelAndWarn(display, ch.channel_id);
                      }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-main-foreground hover:bg-main-text-button-hover-background cursor-pointer text-left transition-colors"
                    >
                      <span className="material-icons !text-sm text-main-muted-foreground">
                        tag
                      </span>
                      <span className="truncate">{ch.channel_name}</span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}

      {botMissingRecipients.size > 0 && (
        <div className="flex items-center gap-1 text-xs text-amber-600 px-1 mt-1">
          <span className="material-icons !text-sm">warning_amber</span>
          Coefficient bot isn&apos;t added to one or more selected channels. Add &quot;Coefficient
          Alerts&quot; to the channel to receive messages.
        </div>
      )}
    </div>
  );
};

// ─── Destination block ────────────────────────────────────────────────────────

export const DestinationBlock: React.FC<{
  destination: SubscriptionDestination;
  onChange: (dest: SubscriptionDestination) => void;
  onRemove: () => void;
  isSlackAuthorized?: boolean;
  isTeamsAuthorized?: boolean;
  onReauthorizeSlack?: () => void;
  onReauthorizeTeams?: () => void;
  autoOpenRecipients?: boolean;
  onAutoOpenRecipientsHandled?: () => void;
}> = ({
  destination,
  onChange,
  onRemove,
  isSlackAuthorized,
  isTeamsAuthorized,
  onReauthorizeSlack,
  onReauthorizeTeams,
  autoOpenRecipients = false,
  onAutoOpenRecipientsHandled,
}) => {
  const { apiClient } = useApiContext();
  const queryClient = useQueryClient();
  const {
    isOpen: showMenu,
    toggle: toggleMenu,
    close: closeMenu,
    style: menuStyle,
    buttonRef,
    menuRef,
  } = useFixedDropdown("left");

  const { data: slackMetaData } = useQuery(
    [QueryCacheKey.SLACK_CHANNELS_AND_USERS],
    () => apiClient.getSlackChannelsAndUsers(),
    {
      enabled: destination.type === "slack" && !!isSlackAuthorized,
      staleTime: 1000 * 60 * 5,
      retry: 1,
    }
  );

  const typeLabel =
    DESTINATION_TYPE_OPTIONS.find(o => o.value === destination.type)?.label || destination.type;

  const recipientPlaceholder =
    destination.type === "email" ? "Add email addresses" : "Add channels";

  const connectedSlackWorkspace = slackMetaData?.data.connected_slack_workspace;

  const refreshList = async () => {
    if (destination.type === "slack") {
      await queryClient.invalidateQueries([QueryCacheKey.SLACK_CHANNELS_AND_USERS]);
    } else if (destination.type === "teams") {
      await queryClient.invalidateQueries([QueryCacheKey.MS_TEAMS_TEAMS_AND_CHANNELS]);
    }
  };

  const reauthorize = () => {
    if (destination.type === "slack") {
      onReauthorizeSlack?.();
    }
    if (destination.type === "teams") {
      onReauthorizeTeams?.();
    }
    closeMenu();
  };

  useEffect(() => {
    if (!autoOpenRecipients) {
      return;
    }
    onAutoOpenRecipientsHandled?.();
  }, [autoOpenRecipients, onAutoOpenRecipientsHandled]);

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DestinationIcon type={destination.type} />
          <span className="text-sm font-semibold text-main-foreground">{typeLabel}</span>
        </div>
        <div className="relative">
          <button
            ref={buttonRef}
            type="button"
            onClick={toggleMenu}
            className="p-1.5 rounded-lg text-main-muted-foreground hover:text-main-foreground hover:bg-main-text-button-hover-background cursor-pointer transition-colors"
            aria-label="Destination options"
          >
            <span className="material-icons !text-[18px]">more_horiz</span>
          </button>
          {showMenu && (
            <div
              ref={menuRef}
              style={menuStyle}
              className="bg-main-background border border-main-border rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.12)] z-[500] w-[200px] max-w-[calc(100vw-24px)] overflow-hidden py-1"
            >
              {destination.type === "email" ? (
                <button
                  type="button"
                  onClick={() => {
                    onRemove();
                    closeMenu();
                  }}
                  className="w-full flex items-center gap-2.5 text-left px-3 py-2.5 text-sm font-semibold text-main-foreground hover:bg-main-text-button-hover-background cursor-pointer transition-colors"
                >
                  <DeleteIcon className="h-4 w-4 shrink-0 text-main-foreground" />
                  Remove
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      onRemove();
                      closeMenu();
                    }}
                    className="w-full flex items-center gap-2.5 text-left px-3 py-2.5 text-sm font-semibold text-main-foreground hover:bg-main-text-button-hover-background cursor-pointer transition-colors"
                  >
                    <DeleteIcon className="h-4 w-4 shrink-0 text-main-foreground" />
                    Remove
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      refreshList();
                      closeMenu();
                    }}
                    className="w-full flex items-center gap-2.5 text-left px-3 py-2.5 text-sm text-main-foreground hover:bg-main-text-button-hover-background cursor-pointer transition-colors"
                  >
                    <span className="material-icons !text-base text-main-muted-foreground">
                      refresh
                    </span>
                    Refresh List
                  </button>
                  <button
                    type="button"
                    onClick={reauthorize}
                    className="w-full flex items-center gap-2.5 text-left px-3 py-2.5 text-sm text-main-foreground hover:bg-main-text-button-hover-background cursor-pointer transition-colors"
                  >
                    <span className="material-icons !text-base text-main-muted-foreground">
                      vpn_key
                    </span>
                    Reauthorize
                  </button>
                  {destination.type === "slack" && connectedSlackWorkspace && (
                    <div className="px-3 py-2.5 border-t border-main-border bg-main-text-button-hover-background/40">
                      <div className="flex items-center gap-1.5 text-main-muted-foreground text-[11px] font-medium uppercase tracking-wide">
                        Slack Workspace
                      </div>
                      <div className="mt-0.5 text-main-foreground text-sm font-medium">
                        <span className="break-all">{connectedSlackWorkspace}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {destination.type === "slack" ? (
        <SlackChannelInputBox
          chips={destination.recipients}
          isAuthorized={isSlackAuthorized}
          autoOpenOnMount={autoOpenRecipients}
          onAdd={r => onChange({ ...destination, recipients: [...destination.recipients, r] })}
          onRemove={r =>
            onChange({ ...destination, recipients: destination.recipients.filter(x => x !== r) })
          }
        />
      ) : destination.type === "teams" ? (
        <MSTeamsChannelInputBox
          chips={destination.recipients}
          isAuthorized={isTeamsAuthorized}
          autoOpenOnMount={autoOpenRecipients}
          onAdd={r => onChange({ ...destination, recipients: [...destination.recipients, r] })}
          onRemove={r =>
            onChange({ ...destination, recipients: destination.recipients.filter(x => x !== r) })
          }
        />
      ) : (
        <RecipientInputBox
          placeholder={recipientPlaceholder}
          chips={destination.recipients}
          validateValue={value => isValidEmail(value)}
          formatValidationError={invalidValues =>
            `Invalid email${invalidValues.length > 1 ? "s" : ""}: ${invalidValues.join(", ")}`
          }
          onAdd={r =>
            onChange({
              ...destination,
              recipients: [...destination.recipients, r.toLowerCase()],
            })
          }
          onRemove={r =>
            onChange({ ...destination, recipients: destination.recipients.filter(x => x !== r) })
          }
        />
      )}
    </div>
  );
};

// ─── Add destination picker ───────────────────────────────────────────────────

export const AddDestinationPicker: React.FC<{
  onAdd: (type: SubscriptionDestinationType) => void;
  /** Destination types already added; each type can only appear once */
  existingTypes: SubscriptionDestinationType[];
}> = ({ onAdd, existingTypes }) => {
  const { isOpen, toggle, close, style, buttonRef, menuRef } = useFixedDropdown("right");
  const available = DESTINATION_TYPE_OPTIONS.filter(opt => !existingTypes.includes(opt.value));

  if (available.length === 0) {
    return null;
  }

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        className="inline-flex items-center gap-1 text-sm font-medium text-primary-button-background hover:opacity-80 cursor-pointer transition-opacity"
      >
        <span className="material-icons !text-[16px]">add</span>
        add destination
      </button>
      {isOpen && (
        <div
          ref={menuRef}
          style={style}
          className="bg-main-background border border-main-border rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.12)] z-[500] min-w-[160px] py-1"
        >
          {available.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onAdd(opt.value);
                close();
              }}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-main-foreground hover:bg-main-text-button-hover-background cursor-pointer transition-colors"
            >
              <DestinationIcon type={opt.value} />
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Teams auth modal ─────────────────────────────────────────────────────────

export const TeamsAuthModal: React.FC<{
  portalDocument: Document;
  open: boolean;
  onAuthorize?: () => void;
  onCancelAuthorize?: () => void;
  onSkip: () => void;
  isAuthorizing?: boolean;
}> = ({ portalDocument, open, onAuthorize, onCancelAuthorize, onSkip, isAuthorizing = false }) => {
  if (!open) {
    return null;
  }

  const modalTree = (
    <div
      className="fixed inset-0 z-[6000] flex items-center justify-center pointer-events-auto"
      onClick={onSkip}
      role="presentation"
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" aria-hidden />
      <div
        className="relative z-10 w-[400px] max-w-[calc(100vw-32px)] rounded-lg bg-main-background p-6 shadow-[0_12px_48px_rgba(0,0,0,0.2)]"
        role="dialog"
        aria-label="Connect Microsoft Teams"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-main-foreground">Connect to Microsoft Teams</h3>
          <button
            type="button"
            onClick={onSkip}
            className="p-1.5 text-main-muted-foreground hover:text-main-foreground rounded-lg hover:bg-main-text-button-hover-background cursor-pointer transition-colors"
            aria-label="Close"
          >
            <span className="material-icons !text-xl">close</span>
          </button>
        </div>

        <div className="flex items-center justify-center gap-4 my-6">
          <ConnectPlugIcon width={72} height={42} />
          <TeamsMicrosoftIcon size={44} />
        </div>

        <div className="text-center">
          <p className="text-base font-semibold text-main-foreground leading-snug mb-1.5">
            Let&apos;s connect to Microsoft Teams
          </p>
          <p className="text-sm text-main-muted-foreground leading-relaxed">
            Authorize Coefficient to connect to Microsoft Teams
          </p>
        </div>

        <div className="mt-6 flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={onAuthorize}
            disabled={!onAuthorize || isAuthorizing}
            className="h-10 px-6 rounded-lg text-sm font-semibold bg-[#5059C9] text-white hover:bg-[#4148b8] disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer transition-colors shadow-sm"
          >
            {isAuthorizing ? "Authorizing..." : "Authorize"}
          </button>
          {isAuthorizing && (
            <button
              type="button"
              onClick={onCancelAuthorize}
              className="text-sm text-main-muted-foreground hover:text-main-foreground cursor-pointer transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalTree, portalDocument.body);
};

export const SlackSubscriptionAuthModal: React.FC<{
  portalDocument: Document;
  open: boolean;
  onAuthorize?: () => void;
  onCancelAuthorize?: () => void;
  onSkip: () => void;
  isAuthorizing?: boolean;
}> = ({ portalDocument, open, onAuthorize, onCancelAuthorize, onSkip, isAuthorizing = false }) => {
  if (!open) {
    return null;
  }

  const modalTree = (
    <div
      className="fixed inset-0 z-[6000] flex items-center justify-center pointer-events-auto"
      onClick={onSkip}
      role="presentation"
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" aria-hidden />
      <div
        className="relative z-10 w-[400px] max-w-[calc(100vw-32px)] rounded-lg bg-main-background p-6 shadow-[0_12px_48px_rgba(0,0,0,0.2)]"
        role="dialog"
        aria-label="Connect to Slack"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-main-foreground">Connect to Slack</h3>
          <button
            type="button"
            onClick={onSkip}
            className="p-1.5 text-main-muted-foreground hover:text-main-foreground rounded-lg hover:bg-main-text-button-hover-background cursor-pointer transition-colors"
            aria-label="Close"
          >
            <span className="material-icons !text-xl">close</span>
          </button>
        </div>

        <div className="flex items-center justify-center gap-4 my-6">
          <ConnectPlugIcon width={72} height={42} />
          <SlackLogo size={44} />
        </div>

        <div className="text-center">
          <p className="text-base font-semibold text-main-foreground leading-snug mb-1.5">
            Let&apos;s connect to Slack
          </p>
          <p className="text-sm text-main-muted-foreground leading-relaxed">
            Authorize Coefficient to connect to Slack
          </p>
        </div>

        <div className="mt-6 flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={onAuthorize}
            disabled={!onAuthorize || isAuthorizing}
            className="h-10 px-6 rounded-lg text-sm font-semibold bg-primary-button-background text-primary-button-foreground hover:bg-primary-button-hover-background disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer transition-colors shadow-sm"
          >
            {isAuthorizing ? "Authorizing..." : "Authorize"}
          </button>
          {isAuthorizing && (
            <button
              type="button"
              onClick={onCancelAuthorize}
              className="text-sm text-main-muted-foreground hover:text-main-foreground cursor-pointer transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalTree, portalDocument.body);
};

// ─── Subscription list item ───────────────────────────────────────────────────

const formatRecipientPreview = (type: SubscriptionDestinationType, raw: string) => {
  const t = raw.trim();
  if (!t) {
    return "";
  }
  if (type === "slack") {
    if (t.startsWith("#") || t.startsWith("@")) {
      return t;
    }
    return `#${t}`;
  }
  return t;
};

const summarizeRecipients = (type: SubscriptionDestinationType, recipients: string[]) => {
  const parts = recipients.map(r => formatRecipientPreview(type, r)).filter(Boolean);
  if (parts.length === 0) {
    return "";
  }
  const shown = parts.slice(0, 2).join(", ");
  const extra = parts.length > 2 ? ` +${parts.length - 2}` : "";
  return `${shown}${extra}`;
};

// Approximate hug width of the actions menu (24px icon + label + 8/32px padding); used only to
// clamp the menu's fixed position within the viewport.
const SUBSCRIPTION_ACTION_MENU_WIDTH_PX = 140;

const formatLastRunTime = (dt: string): string => {
  const d = new Date(dt);
  const isToday = new Date().toDateString() === d.toDateString();
  const timeStr = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return isToday
    ? `Today at ${timeStr}`
    : `${d.toLocaleDateString([], { month: "short", day: "numeric" })} at ${timeStr}`;
};

export const SubscriptionListItem: React.FC<{
  subscription: DashboardSubscription;
  targetDocument: Document;
  onEdit: () => void;
  onPause?: () => void;
  onDelete?: () => void;
}> = ({ subscription, targetDocument, onEdit, onPause, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const updateMenuPosition = useCallback(() => {
    const btn = menuButtonRef.current;
    if (!btn) {
      return;
    }
    const rect = btn.getBoundingClientRect();
    const vw =
      targetDocument.defaultView?.innerWidth ??
      (typeof window !== "undefined" ? window.innerWidth : 0);
    const gap = 4;
    let left = rect.right + gap;
    const maxLeft = vw - SUBSCRIPTION_ACTION_MENU_WIDTH_PX - 8;
    if (left > maxLeft) {
      left = maxLeft;
    }
    if (left < 8) {
      left = 8;
    }
    setMenuPosition({ top: rect.bottom + 4, left });
  }, [targetDocument]);

  useLayoutEffect(() => {
    if (!menuOpen) {
      setMenuPosition(null);
      return;
    }
    updateMenuPosition();
    const win = targetDocument.defaultView;
    if (!win) {
      return;
    }
    win.addEventListener("resize", updateMenuPosition);
    win.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      win.removeEventListener("resize", updateMenuPosition);
      win.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [menuOpen, updateMenuPosition, targetDocument]);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }

    const doc = targetDocument;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false);
      }
    };

    doc.addEventListener("click", handleClickOutside, true);
    return () => doc.removeEventListener("click", handleClickOutside, true);
  }, [menuOpen, targetDocument]);

  const destinationsWithRecipients = subscription.destinations.filter(d => d.recipients.length > 0);

  return (
    <div
      className="group flex items-center gap-3 p-3 bg-main-background-secondary rounded-xl transition-colors cursor-pointer"
      onClick={onEdit}
    >
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        {/* min-h reserves the badge's height so toggling paused doesn't change the row height
            and shift the content below. */}
        <div className="flex items-center gap-2 min-w-0 min-h-[20px]">
          <span className="text-sm font-bold text-main-foreground truncate">
            {subscription.scheduleLabel}
          </span>
          {subscription.paused && (
            <span className="inline-flex items-center px-2 h-[20px] rounded border border-main-foreground text-[11px] font-semibold leading-[14px] text-main-foreground uppercase shrink-0">
              Paused
            </span>
          )}
        </div>
        {destinationsWithRecipients.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 min-w-0">
            {destinationsWithRecipients.map(dest => (
              <div key={dest.type} className="flex items-center gap-1 min-w-0">
                <DestinationIcon type={dest.type} size="sm" />
                <span className="text-sm font-medium text-main-muted-foreground truncate">
                  {summarizeRecipients(dest.type, dest.recipients)}
                </span>
              </div>
            ))}
          </div>
        )}
        {subscription.lastRunDt && (
          <div className="flex flex-col gap-0.5 min-w-0 mt-0.5">
            {subscription.lastRunFailed ? (
              <>
                <div className="flex items-center gap-1 min-w-0">
                  <span className="material-icons !text-[14px] text-red-500 shrink-0">error</span>
                  <span className="text-xs text-red-600 truncate">
                    Last send failed. {formatLastRunTime(subscription.lastRunDt)}
                  </span>
                </div>
                {subscription.lastRunErrorMessage && (
                  <span className="text-[11px] text-red-500 break-words pl-5">
                    {subscription.lastRunErrorMessage}
                  </span>
                )}
              </>
            ) : (
              <span className="text-xs text-main-muted-foreground truncate">
                Last sent {formatLastRunTime(subscription.lastRunDt)}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="relative shrink-0 flex items-center">
        <button
          ref={menuButtonRef}
          type="button"
          title="Subscription actions"
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          onClick={e => {
            e.stopPropagation();
            setMenuOpen(v => !v);
          }}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-main-foreground hover:bg-main-text-button-hover-background cursor-pointer transition-colors"
        >
          <EllipsisMenu width="16" height="16" fill="currentColor" />
        </button>
        {menuOpen && menuPosition && targetDocument.body
          ? createPortal(
              <div
                ref={menuRef}
                className="w-max min-w-[160px] flex flex-col py-1 overflow-hidden shadow-[0_0_8px_rgba(0,0,0,0.08),0_16px_12px_rgba(0,0,0,0.08)] rounded-xl bg-main-background"
                role="menu"
                style={{
                  position: "fixed",
                  top: menuPosition.top,
                  left: menuPosition.left,
                  zIndex: 6000,
                }}
                onClick={e => e.stopPropagation()}
              >
                <button
                  type="button"
                  role="menuitem"
                  className="w-full px-3 py-2 text-left text-sm font-semibold flex items-center gap-1.5 transition-colors cursor-pointer text-main-foreground hover:bg-main-text-button-hover-background"
                  onClick={e => {
                    e.stopPropagation();
                    setMenuOpen(false);
                    onEdit();
                  }}
                >
                  <span className="flex size-[24px] shrink-0 items-center justify-center">
                    <PencilIcon width="16" height="16" />
                  </span>
                  Edit
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="w-full px-3 py-2 text-left text-sm font-semibold flex items-center gap-1.5 transition-colors cursor-pointer text-main-foreground hover:bg-main-text-button-hover-background"
                  onClick={e => {
                    e.stopPropagation();
                    setMenuOpen(false);
                    onPause?.();
                  }}
                >
                  <span className="flex size-[24px] shrink-0 items-center justify-center">
                    {subscription.paused ? (
                      <ResumeIcon width="16" height="16" />
                    ) : (
                      <PauseIcon width="16" height="16" />
                    )}
                  </span>
                  {subscription.paused ? "Resume" : "Pause"}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="w-full px-3 py-2 text-left text-sm font-semibold flex items-center gap-1.5 transition-colors cursor-pointer text-main-foreground hover:bg-main-text-button-hover-background"
                  onClick={e => {
                    e.stopPropagation();
                    setMenuOpen(false);
                    onDelete?.();
                  }}
                >
                  <span className="flex size-[24px] shrink-0 items-center justify-center">
                    <DeleteIcon width="16" height="16" />
                  </span>
                  Delete
                </button>
              </div>,
              targetDocument.body
            )
          : null}
      </div>
    </div>
  );
};
